import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { API_URL } from './shared';

const emptyItem = { name: '', quantity: 1, price: 0, selected_size: '', selected_phone_model: '', category: '' };

const ManualOrderModal = ({ open, onOpenChange, token, onCreated }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    shipping_method: 'collect',
    shipping_cost: 0,
    discount_amount: 0,
    payment_status: 'pending_payment',
    order_status: 'pending',
    notes: '',
    send_packing_slip: false,
    items: [{ ...emptyItem }],
  });

  const updateItem = (idx, patch) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...prev, items };
    });
  };

  const addItem = () => setForm((p) => ({ ...p, items: [...p.items, { ...emptyItem }] }));
  const removeItem = (idx) => setForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.price) || 0), 0);
  const total = subtotal - (Number(form.discount_amount) || 0) + (Number(form.shipping_cost) || 0);

  const reset = () => setForm({
    customer_name: '', customer_email: '', customer_phone: '', customer_address: '',
    shipping_method: 'collect', shipping_cost: 0, discount_amount: 0,
    payment_status: 'pending_payment', order_status: 'pending', notes: '',
    send_packing_slip: false, items: [{ ...emptyItem }],
  });

  const submit = async () => {
    if (!form.customer_name || !form.customer_email) {
      toast.error('Customer name and email are required');
      return;
    }
    if (!form.items.length || form.items.some((i) => !i.name)) {
      toast.error('Every item needs a name');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((i) => ({
          name: i.name,
          quantity: Number(i.quantity) || 1,
          price: Number(i.price) || 0,
          selected_size: i.selected_size || null,
          selected_phone_model: i.selected_phone_model || null,
          category: i.category || null,
        })),
        shipping_cost: Number(form.shipping_cost) || 0,
        discount_amount: Number(form.discount_amount) || 0,
      };
      const res = await axios.post(`${API_URL}/admin/orders`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Manual order created');
      reset();
      onOpenChange(false);
      onCreated?.(res.data?.order);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d?.msg).join('; ') : 'Failed to create order';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Create Manual Order</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2" data-testid="manual-order-form">
          {/* Customer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Customer name *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="manual-order-name" />
            <Input placeholder="Customer email *" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="manual-order-email" />
            <Input placeholder="Phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="manual-order-phone" />
            <Input placeholder="Address (optional)" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="manual-order-address" />
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Items</label>
              <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="manual-order-add-item">
                <Plus className="w-4 h-4 mr-1" /> Add item
              </Button>
            </div>
            <div className="space-y-2">
              {form.items.map((item, idx) => (
                <div key={`mo-item-${idx}`} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-5" placeholder="Item name *" value={item.name} onChange={(e) => updateItem(idx, { name: e.target.value })} data-testid={`manual-order-item-name-${idx}`} />
                  <Input className="col-span-2" type="number" min="1" placeholder="Qty" value={item.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`manual-order-item-qty-${idx}`} />
                  <Input className="col-span-2" type="number" step="0.01" placeholder="Price (R)" value={item.price} onChange={(e) => updateItem(idx, { price: e.target.value })} data-testid={`manual-order-item-price-${idx}`} />
                  <Input className="col-span-2" placeholder="Size/Phone" value={item.selected_size || item.selected_phone_model} onChange={(e) => updateItem(idx, { selected_size: e.target.value })} data-testid={`manual-order-item-extra-${idx}`} />
                  <Button type="button" size="sm" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeItem(idx)} data-testid={`manual-order-remove-item-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Costs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Shipping method</label>
              <Select value={form.shipping_method} onValueChange={(v) => setForm({ ...form, shipping_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="collect">Collect</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Shipping cost (R)</label>
              <Input type="number" step="0.01" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} data-testid="manual-order-shipping" />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Discount (R)</label>
              <Input type="number" step="0.01" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} data-testid="manual-order-discount" />
            </div>
          </div>

          {/* Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Payment status</label>
              <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger data-testid="manual-order-payment-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending_payment">Pending payment</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Order status</label>
              <Select value={form.order_status} onValueChange={(v) => setForm({ ...form, order_status: v })}>
                <SelectTrigger data-testid="manual-order-order-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-600 mb-1 block">Notes (optional)</label>
            <textarea className="w-full border rounded-md p-2 text-sm" rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="manual-order-notes" />
          </div>

          {/* Packing slip toggle */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.send_packing_slip} onChange={(e) => setForm({ ...form, send_packing_slip: e.target.checked })} data-testid="manual-order-send-packing" />
            <span>Send packing slip to warehouse on save <span className="text-gray-400">(only if marked Paid)</span></span>
          </label>

          {/* Totals */}
          <div className="bg-gray-50 rounded-md p-3 text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>R{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Discount</span><span>− R{(Number(form.discount_amount) || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>R{(Number(form.shipping_cost) || 0).toFixed(2)}</span></div>
            <div className="flex justify-between font-semibold border-t pt-1 mt-1 text-[#0A2342]"><span>Total</span><span>R{total.toFixed(2)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90" data-testid="manual-order-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Create Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualOrderModal;
